import { getClientLogoUrl } from './clientLogo';

/** In-memory signed URL cache — avoids initials→image flash on navigation. */
const TTL_MS = 50 * 60 * 1000;

type CacheEntry = { url: string; expiresAt: number };

const urlCache = new Map<string, CacheEntry>();
const imageReady = new Set<string>();

export function getCachedClientLogoUrl(logoPath: string): string | null {
  const key = logoPath.trim();
  const entry = urlCache.get(key);
  if (!entry) return null;
  if (entry.expiresAt <= Date.now()) {
    urlCache.delete(key);
    imageReady.delete(entry.url);
    return null;
  }
  return entry.url;
}

export function setCachedClientLogoUrl(logoPath: string, url: string): void {
  const key = logoPath.trim();
  const prev = urlCache.get(key);
  if (prev && prev.url !== url) {
    imageReady.delete(prev.url);
  }
  urlCache.set(key, { url, expiresAt: Date.now() + TTL_MS });
}

export function invalidateClientLogoCache(logoPath?: string | null): void {
  if (!logoPath?.trim()) {
    urlCache.clear();
    imageReady.clear();
    return;
  }
  const key = logoPath.trim();
  const entry = urlCache.get(key);
  if (entry) imageReady.delete(entry.url);
  urlCache.delete(key);
}

export function isClientLogoImageReady(url: string): boolean {
  return imageReady.has(url);
}

export function markClientLogoImageReady(url: string): void {
  imageReady.add(url);
}

function preloadImage(url: string): Promise<void> {
  if (imageReady.has(url)) return Promise.resolve();
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      imageReady.add(url);
      resolve();
    };
    img.onerror = () => resolve();
    img.src = url;
  });
}

export async function prefetchClientLogos(
  paths: Iterable<string | null | undefined>,
): Promise<void> {
  const unique = [
    ...new Set(
      [...paths]
        .map((p) => p?.trim())
        .filter((p): p is string => Boolean(p)),
    ),
  ];

  await Promise.all(
    unique.map(async (path) => {
      let url = getCachedClientLogoUrl(path);
      if (!url) {
        url = await getClientLogoUrl(path);
        if (url) setCachedClientLogoUrl(path, url);
      }
      if (url) await preloadImage(url);
    }),
  );
}
